import axios from 'axios'
import { ConcurrencyManager } from 'axios-concurrency'
import axiosRetry, { isNetworkError,  isRetryableError, exponentialDelay } from 'axios-retry'
import { format } from 'date-fns'
import { Subscribe } from './Pusher'
import { throttle } from 'lodash'
import Cookies from 'js-cookie'
import { emitLoggedOut } from './AppEvents'

const xsrfCookieName = "admin-xsrf-token"
const xsrfHeaderName = "x-admin-xsrf-token"

const baseConfig = {
  baseURL: process.env.REACT_APP_ENDPOINT_BASE_URL,
  withCredentials: true,
  headers: {'x-no-escape': 'yes'},
}

class LoggedOutError extends Error {
  constructor() {
    super('logged out')
    this.loggedOut = true
  }
}

function createClient({useConcurrencyManager}){
  const client = axios.create(baseConfig)

  client.interceptors.request.use((config) => {
    if (xsrfCookieName && isSameSite(window.location.origin, config.url)) {
      const xsrfValue = Cookies.get(xsrfCookieName)
      if (xsrfValue) {
        config.headers.set(xsrfHeaderName, xsrfValue)
      }
    }
    return config
  })

  client.interceptors.response.use(async (response) => {
    if (!WHITE_LIST_URLS.includes(response.config.url) && response.data && !response.data.success) {
      const { data } = await GetThrottledSessionStatus()
      if (!data.logged_in) throw new LoggedOutError()
    }
    return response
  })

  axiosRetry(client, {
    retries: 3,
    retryDelay: exponentialDelay,
    retryCondition: (error) => {
      if (error.status === 200 || error.status === 403) {
        return false
      } else {
        return isNetworkError(error) || isRetryableError(error)
      }
    },
    onRetry: (retryCount, _error, requestConfig) => {
      if (retryCount === 2) {
        requestConfig.baseURL = ''
      }
    },
  })
  if (useConcurrencyManager) ConcurrencyManager(client, 3)

  return client
}

const ax = createClient({useConcurrencyManager: false})
const axQ = createClient({useConcurrencyManager: true})

const isSameSite = function (urlAStr, urlBStr) {
  if (urlAStr && urlBStr) {
    const urlA = new URL(urlAStr, window.location.origin)
    const urlB = new URL(urlBStr, window.location.origin)
    return (
      urlA.hostname.split(/\./).slice(-3).join(".") === urlB.hostname.split(/\./).slice(-3).join(".") &&
      urlA.protocol === urlB.protocol
    )
  } else {
    return false
  }
}

// Requests to these URLs should not be intercepted when 'success' is false
const WHITE_LIST_URLS = ['/api/v1/session/status']

let channel

const ListDataRooms = async ({ setDataRooms, setLoading }, rooms = [], cursor) => {
  setLoading && setLoading(true)
  const result = await ax.post(
    '/api/v1/data_rooms/list', {
      extensive: true,
      admin_only: true,
      max_num: 20,
      cursor,
    })

  if(result.data.success === false) throw new Error(result.data.info)
  const fetchedRooms = [...rooms, ...result.data.data_rooms]
        .sort((a,b) => a.data_room_display_name.localeCompare(b.data_room_display_name, 'sv', { numeric: true, sensitivity: 'base' }))

  if (result.data.cursor) {
    ListDataRooms({setDataRooms, setLoading}, fetchedRooms, result.data.cursor)
  } else {
    setLoading && setLoading(false)
  }

  setDataRooms(fetchedRooms)
}

const GetSessionStatus = async () => {
  const { data } = await ax.post('/api/v1/session/status', {}, {'axios-retry': {retries: 0}})
  if (!data.logged_in) {
    emitLoggedOut('You have been logged out, possibly due to inactivity. Please log in again.')
  }
  return { data }
}

const GetThrottledSessionStatus = throttle(GetSessionStatus, 10 * 1000)

const AddRoom = async ({
  billing_details,
  data_room_name,
  data_room_public_name,
  light,
  qa,
  searchable,
  searchable_content,
  two_factor_allowed,
  source_data_room,
}) => ax.post('/api/v1/data_rooms/add', {
  billing_details,
  data_room_name,
  data_room_public_name,
  light,
  qa,
  searchable,
  searchable_content,
  two_factor_allowed,
  source_data_room,
}, {'axios-retry': {retries: 1}})

// Mirrors the channel-subscription block inside CreateOperation. Both call sites
// must pass the same stable dispatch reference (messageDispatch from
// MessageContext); a future caller passing a different dispatch would silently
// be ignored because Subscribe binds the dispatch from the first invocation.
const subscribeToSessionChannel = async (dispatch) => {
  if (!channel) {
    const { data } = await sessionChannel()
    channel = data
    Subscribe(channel, dispatch)
  }
}

const UpdateRoom = async room => ax.post('/api/v1/data_rooms/edit', room)

const getUploadLogoLink = async () => ax.get(`/api/v1/uploads/get_upload_url_indirect?dir=logo.upload&time=${format(new Date(), 'T')}`)

const UploadLogo = async ({ data_room, file }) => {
  const { data } = await getUploadLogoLink()
  let bodyFormData = new FormData()
  bodyFormData.append('data_room', data_room)
  bodyFormData.append('files[]', file)
  return ax({
    method: 'post',
    url: data,
    data: bodyFormData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

const ResetLogo = ({ data_room }) => ax.get(`/api/v1/uploads/register_logo_indirect?no_logo=true&data_room=${data_room}`)

const CreateOperation = async ({incl_unaccepted, incl_unpublished_a, incl_attention_from, incl_creators, incl_detailed_activity, incl_status_int_less_or_equal_than, include_index, split_long_paths, request_original, url, stage_label, data_room_display_name, data_room, q_branch, billing_id, kind}, dispatch) => {
  const {
    data: { operation_id, success },
  } = await ax.post(url, {incl_unaccepted, incl_unpublished_a, incl_attention_from, incl_creators, incl_detailed_activity, incl_status_int_less_or_equal_than, include_index, split_long_paths, request_original, data_room, q_branch, billing_id, kind})
  dispatch({
    type: 'create operation',
    payload: { stage_label, data_room_display_name, data_room, operation_id },
  })
  if (!channel) {
    const { data } = await sessionChannel()
    channel = data
    Subscribe(channel, dispatch)
  }
  return { data: { operation_id, success }}
}

const sessionChannel = () => ax.post('/api/v1/session/channel', {})

const ListUserTypes = async ({ data_room }) => ax.post('/api/v1/users/list_types', { data_room })

const ListUsers = async ({ user_type }) => ax.post('/api/v1/users/list', { user_type })

const EditUserType = async ({ data_room, user_type, user_type_name, deleted }) =>
  ax.post('/api/v1/users/edit_type', {
    data_room,
    user_type,
    user_type_name,
    deleted,
  })

const TypeSettingsUser = async ({
  user_type,
  send_file_update_notification,
  allow_index,
  allow_dr_download,
  f_disclaimer,
  a_disclaimer,
}) =>
  ax.post('/api/v1/users/type_settings', {
    user_type,
    send_file_update_notification,
    allow_index,
    allow_dr_download,
    f_disclaimer,
    a_disclaimer,
  })

const InviteUsers = async ({ user_type, invite_content, no_mail }) =>
  ax.post('/api/v1/users/invite', { user_type, invite_content, no_mail }, {'axios-retry': {retries: 1}})

const EditUserTypeRel = async ({ user, user_name, user_type, remove, two_factor_phone_number, force }) =>
  ax.post('/api/v1/users/edit_user_type_rel', {
    user,
    user_name,
    user_type,
    remove,
    two_factor_phone_number,
    force,
  })

const QAListCategories = async ({ data_room, q_branch, assignable }) =>
  ax.post('/api/v1/qa/list_categories', {
    data_room,
    q_branch,
    assignable
  })

const QAGetCategory = async ({ category }) =>
  ax.post('/api/v1/qa/category', {
    category
  })

const QAAddEditCategory = async ({ category, closed, data_room, q_branch, name, deleted, rights, pipeline }) =>
  ax.post('/api/v1/qa/add_edit_category', {
    category,
    closed,
    data_room,
    q_branch,
    name,
    deleted,
    rights,
    pipeline,
  })

const QAAddEditCategoryBatch = async ({ rights, category }) => {
  let latestData
  for (const right of rights) {
    const { data } = await QAAddEditCategory({
      category: category,
      rights: [
        {
          user_type: right.user_type,
          s_rights: right.s_rights,
          q_export: right.q_export,
        },
      ],
    })
    if (!data.success) throw new Error(data.info)
    latestData = data
  }
  return latestData
}

const QAMemberLookup = async ({data_room, include_data_room = true, include_user_types = true, include_users = true}) =>
  ax.post(
    '/api/v1/qa/member_lookup', {
      data_room,
      include_data_room,
      include_user_types,
      include_users,
    })

const QAListBranches = async ({ data_room }) =>
  ax.post('/api/v1/qa/list_branches', {
    data_room
  })

const QAAddEditBranch = async ({ q_branch, data_room, name, deleted }) =>
  ax.post('/api/v1/qa/add_edit_branch', {
    q_branch,
    data_room,
    name,
    deleted,
  })

const QAListBranchRights = async ({ data_room, user_type }) =>
  ax.post('/api/v1/qa/list_branch_rights', {
    data_room,
    user_type,
  })

const FoldersGetContents = async params => {
  const request_params = {
    incl_files: false,
    incl_folders: true,
    incl_super_folders: false,
    incl_download_path: false,
    incl_special_folders: false,
    ...params,
  }
  return axQ.post('/api/v1/folders/list_contents', request_params, {'axios-retry': {retries: 3}})
}

const UsersEditFolderRights = async params => ax.post('/api/v1/users/edit_folder_rights', params)

const FoldersEditAutoRedact = async params => ax.post('/api/v1/folders/edit_auto_redact', params)

const StatsListQueryGroups = async ({ data_room }) => ax.post('/api/v1/stats/list_query_groups', { data_room })

const StatsQueryDetails = async ({ data_room, user_name, user_type_name, breakpoints }) =>
  ax.post('/api/v1/stats/query_details', { data_room, user_name, user_type_name, breakpoints })

const StatsQuery = async ({ data_room, user_name, user_type_name, breakpoints }) =>
  ax.post('/api/v1/stats/query', { data_room, user_name, user_type_name, breakpoints })

const CreateUserLink = async params => ax.post('/api/v1/users/create_link', params)

const BillingListDataRooms = async ({ cursor, billing_id, max_num, user_name, seen_data_rooms }) => ax.post('/api/v1/billing/list_data_rooms', {cursor, billing_id, max_num, user_name, seen_data_rooms})

const BillingListBillingIDs = async () => ax.post('/api/v1/billing/list_billing_ids', {})

const BillingDataRoomInfo = async ({ data_room }) => ax.post('/api/v1/billing/data_room_info', {data_room})

const BillingBillingIDInfo = async ({ billing_id }) => ax.post('/api/v1/billing/billing_id_info', {billing_id})

const BillingInviteBillingID = async ({ billing_id, invite_content, no_mail }) => ax.post('/api/v1/billing/invite_billing_id', {billing_id, invite_content, no_mail})

const BillingEditUserRel = async ({ user, user_name, billing_id, remove }) => ax.post('/api/v1/billing/edit_user_rel', {user, user_name, billing_id, remove})

const BillingRequestRestore = async ({ data_room }) => ax.post('/api/v1/billing/request_restore', {data_room})

const BillingUserInfo = async ({ user_name }) => ax.post('/api/v1/billing/user_info', {user_name})

const BillingEditBillingID = async ({ billing_id, enforce_tfa }) => ax.post('/api/v1/billing/edit_billing_id', {billing_id, enforce_tfa})

const BillingResetPassword = async ({ billing_id, user_name }) => ax.post('/api/v1/billing/reset_password', {billing_id, user_name})

const BillingAddComment = async ({ data_room, comment }) => ax.post('/api/v1/billing/add_comment', {data_room, comment})

export {
  ListDataRooms,
  GetSessionStatus,
  AddRoom,
  UpdateRoom,
  UploadLogo,
  ResetLogo,
  CreateOperation,
  subscribeToSessionChannel,
  ListUserTypes,
  ListUsers,
  TypeSettingsUser,
  EditUserType,
  InviteUsers,
  EditUserTypeRel,
  QAListCategories,
  QAGetCategory,
  QAAddEditCategory,
  QAAddEditCategoryBatch,
  QAMemberLookup,
  FoldersGetContents,
  UsersEditFolderRights,
  FoldersEditAutoRedact,
  StatsListQueryGroups,
  StatsQueryDetails,
  StatsQuery,
  CreateUserLink,
  BillingListDataRooms,
  BillingListBillingIDs,
  BillingDataRoomInfo,
  BillingBillingIDInfo,
  BillingInviteBillingID,
  BillingEditUserRel,
  BillingRequestRestore,
  BillingUserInfo,
  BillingEditBillingID,
  BillingResetPassword,
  QAListBranches,
  QAAddEditBranch,
  QAListBranchRights,
  BillingAddComment,
}
